import { geocodeByAddress } from 'react-places-autocomplete'

import Window from 'types/window.d'

import isDebug from 'utils/isDebug'

import {
  AddressComponent,
  ApiResponse,
  CityAndState,
  Geocode,
  GeocodeAddress,
  GeocoderResult,
  SimpleGeocodeData,
} from './types'

export * from './context'

export * from './types'

declare let window: Window

export const getGeocode = async (address: string): Promise<Geocode|undefined> => {
  const geocoderResult = await get(address)

  if (!Array.isArray(geocoderResult)) {
    return undefined
  }

  const geocodeAddress = {
    city: getCityFromGeocoderResult(geocoderResult[0]),
    county: getCountyFromGeocoderResult(geocoderResult[0]),
    state: getStateFromGeocoderResult(geocoderResult[0]),
    streetName: getStreetNameFromGeocoderResult(geocoderResult[0]),
    streetNumber: getStreetNumberFromGeocoderResult(geocoderResult[0]),
    zip5: getZipCodeFromGeocoderResult(geocoderResult[0]),
    zip4: getZipCodeSuffixFromGeocoderResult(geocoderResult[0]),
  }

  const compiledGeocode: SimpleGeocodeData = {
    isLoading: false,
    isValid: false,
    city: (geocodeAddress.city?.long_name) || undefined,
    county: (geocodeAddress.county?.long_name && geocodeAddress.county.long_name.replace(/ County$/, '')) || undefined,
    stateAbbr: (geocodeAddress.state?.short_name) || undefined,
    streetName: (geocodeAddress.streetName?.long_name) || undefined,
    streetNumber: (geocodeAddress.streetNumber?.long_name) || undefined,
    zip5: (geocodeAddress.zip5?.long_name) || undefined,
    zip4: (geocodeAddress.zip4?.long_name) || undefined,
    zipCode: (geocodeAddress.zip5 && geocodeAddress.zip4 && `${geocodeAddress.zip5.long_name || ''}-${geocodeAddress.zip4.long_name || ''}`) || geocodeAddress.zip5?.long_name || undefined,
  }

  return {
    data: {
      ...compiledGeocode,
      isValid: isValid(compiledGeocode),
    },
    originalData: {
      city: geocodeAddress.city,
      county: geocodeAddress.county,
      state: geocodeAddress.state,
      streetName: geocodeAddress.streetName,
      streetNumber: geocodeAddress.streetNumber,
      zip5: geocodeAddress.zip5,
      zip4: geocodeAddress.zip4,
    } as GeocodeAddress,
  } as Geocode
}

export const getCityAndStateFromZipCode = async (zipCode: string): Promise<CityAndState> => {
  const response = await get(zipCode)

  if (!response || !Array.isArray(response) || !response.length) {
    return undefined
  }

  const result = response.find(result => {
    const postalCode = result.address_components.find(addressComponent =>
      addressComponent.types.includes('postal_code'),
    )

    if (!postalCode) {
      return false
    }

    return postalCode.short_name === zipCode.substring(0, 5)
  })

  if (!result) {
    return undefined
  }

  const country = getCountryFromGeocoderResult(result)

  if (!country || country.short_name !== 'US') {
    return undefined
  }

  const city = getCityFromGeocoderResult(result)

  const state = getStateFromGeocoderResult(result)

  return {
    city: (city && city.long_name) || undefined,
    isValid: true,
    state:
      (state && {
        abbr: state.short_name,
        name: state.long_name,
      }) ||
      undefined,
  }
}

const getCityFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('locality'),
  )

const getCountyFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('administrative_area_level_2'),
  )

const getStateFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('administrative_area_level_1'),
  )

const getStreetNameFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('route'),
  )

const getStreetNumberFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('street_number'),
  )

const getZipCodeFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('postal_code'),
  )

const getZipCodeSuffixFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('postal_code_suffix'),
  )

const getCountryFromGeocoderResult = (result: GeocoderResult): AddressComponent | undefined =>
  result.address_components.find(addressComponent =>
    addressComponent.types.includes('country'),
  )

const get = async (address: string): Promise<ApiResponse> => {
  if (!getIsInitialized()) {
    isDebug && console.error('Google Maps is not initialized. Did you forget to wrap your component with GoogleMapsContextProvider?') // eslint-disable-line no-console

    return Promise.reject({ error: 'Google Maps is not initialized.' })
  }

  return geocodeByAddress(address)
    .then((resp: GeocoderResult[]) => resp)
    .catch(error => {
      isDebug && console.error('Google Geocoding API Error', error) // eslint-disable-line no-console

      return { error }
    })
}

export const getIsInitialized = () => !!(typeof window !== 'undefined' && typeof window.google !== 'undefined' && window.google.maps)

export const isValid = (geocode: GeocodeAddress) => (geocode.city && geocode.county && geocode.stateAbbr && geocode.streetName && geocode.streetNumber && geocode.zip5 && true) || false
